import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveImagePath } from '@/utils/imageMap';
import { Search, ShoppingCart, Menu, X } from 'lucide-react';

interface NavigationItem {
  label: string;
  href: string;
  submenu?: NavigationItem[];
}

const navigationItems: NavigationItem[] = [
  {
    label: 'Home',
    href: '/',
  },
  {
    label: 'Shop',
    href: '#',
    submenu: [
      {
        label: 'Quicksilver Ultralight Aircraft',
        href: '/quicksilver-aircraft',
        submenu: [
          { label: 'Air-Tech Exclusives', href: '/air-tech-exclusives' },
          { label: 'AERO1000 Engine', href: '/aero1000' },
          { label: 'Airframe Parts & Assemblies', href: '/product-category/airframe-parts-assemblies' },
          { label: 'Avionics', href: '/product-category/avionics' },
          { label: 'Axles', href: '/axles' },
        ]
      },
      {
        label: 'Books, Videos & Labels',
        href: '/product-category/books-videos-labels',
        submenu: [
          { label: 'Cables & Supplies', href: '/product-category/cables-supplies' },
          { label: 'Electrical', href: '/product-category/electrical' },
          { label: 'Engine Stuff', href: '/product-category/engine-stuff' },
        ]
      },
      {
        label: 'Floats',
        href: '/product-category/floats',
        submenu: [
          { label: 'Fuel Systems', href: '/product-category/fuel-systems' },
          { label: 'Hardware', href: '/product-category/hardware' },
          { label: 'Propellers', href: '/product-category/propellers' },
        ]
      },
      {
        label: 'Sail Sets',
        href: '/product-category/quicksilver-sails-sets',
        submenu: [
          { label: 'Seats, Covers & Belts', href: '/product-category/seats-covers-belts' },
          { label: 'Tools & Supplies', href: '/product-category/tools-supplies' },
          { label: 'Wheels, Tires & Brakes', href: '/product-category/wheels-tires-brakes' },
        ]
      }
    ]
  },
  {
    label: 'Parts Manuals',
    href: '/assembly-manuals',
  },
  {
    label: 'Resources',
    href: '#',
    submenu: [
      { label: 'Video', href: '/video' },
      { label: 'Assembly Manuals – Free Downloadable PDFs', href: '/assembly-manuals' },
      { label: 'How to Videos', href: '/category/howtovideos' },
      { label: 'Product Images', href: '/category/image-gallery' },
    ]
  },
  {
    label: 'About Us',
    href: '#',
    submenu: [
      { label: 'About Air-Tech Inc', href: '/about-us' },
      { label: 'Terms & Conditions of Sale', href: '/terms-of-sale' },
      { label: 'News', href: '/bever-news' },
      { label: 'Contact Us', href: '/contact-us' },
    ]
  },
  {
    label: 'My Account',
    href: '/my-account',
    submenu: [
      { label: 'Login / Account', href: '/my-account/edit-account' },
      { label: 'Orders', href: '/my-account/orders' },
      { label: 'Your Shopping Cart', href: '/cart' },
      { label: 'Checkout', href: '/checkout' },
    ]
  },
  {
    label: 'Blow Out Sale',
    href: '/product-category/blow-out-sale',
  },
];

interface DropdownMenuProps {
  items: NavigationItem[];
  isOpen: boolean;
  onClose: () => void;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ items, isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="absolute top-full left-0 bg-white shadow-lg border border-gray-200 min-w-[250px] z-50">
      {items.map((item, index) => (
        <div key={index} className="relative group">
          <Link
            to={item.href}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600"
            onClick={onClose}
          >
            {item.label}
          </Link>
          {item.submenu && (
            <div className="absolute left-full top-0 bg-white shadow-lg border border-gray-200 min-w-[250px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              {item.submenu.map((subItem, subIndex) => (
                <Link
                  key={subIndex}
                  to={subItem.href}
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                  onClick={onClose}
                >
                  {subItem.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Header: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement search functionality
    console.log('Search query:', searchQuery);
  };

  const handleDropdownToggle = (index: number) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const logoSrc = resolveImagePath('/assets/images/airtechlogo.png');

  return (
    <header className="bg-[#315394] text-white">
      {/* Main Header */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={logoSrc}
              alt="Air-Tech Inc. – Quicksilver Ultralight Aircraft"
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigationItems.map((item, index) => (
              <div key={index} className="relative group">
                {item.submenu ? (
                  <button
                    className="text-white hover:text-yellow-300 font-bold text-sm transition-colors duration-200 py-2 flex items-center"
                    onClick={() => handleDropdownToggle(index)}
                  >
                    {item.label}
                    <svg className="ml-1 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className="text-white hover:text-yellow-300 font-bold text-sm transition-colors duration-200 py-2"
                  >
                    {item.label}
                  </Link>
                )}
                
                {item.submenu && (
                  <DropdownMenu
                    items={item.submenu}
                    isOpen={openDropdown === index}
                    onClose={() => setOpenDropdown(null)}
                  />
                )}
              </div>
            ))}
          </nav>

          {/* Search and Cart */}
          <div className="flex items-center space-x-4">
            {/* Search Form */}
            <form onSubmit={handleSearch} className="hidden md:flex items-center">
              <div className="relative">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  className="pl-3 pr-10 py-2 text-sm bg-white text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 outline-none"
                />
                <button
                  type="submit"
                  className="absolute right-1 top-1 bottom-1 px-2 text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>
            </form>

            {/* Cart Icon */}
            <Link
              to="/cart"
              className="text-white hover:text-yellow-300 transition-colors duration-200"
              title="View your shopping cart"
            >
              <ShoppingCart className="h-6 w-6" />
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white hover:text-yellow-300 transition-colors duration-200"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-blue-400">
            <div className="py-4 space-y-2">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                  <input
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search"
                    className="w-full pl-3 pr-10 py-2 text-sm bg-white text-gray-900 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-300 focus:border-yellow-300 outline-none"
                  />
                  <button
                    type="submit"
                    className="absolute right-1 top-1 bottom-1 px-2 text-gray-600 hover:text-blue-600 transition-colors"
                  >
                    <Search className="h-4 w-4" />
                  </button>
                </div>
              </form>

              {/* Mobile Navigation */}
              {navigationItems.map((item, index) => (
                <div key={index}>
                  <Link
                    to={item.href}
                    className="block py-2 text-white hover:text-yellow-300 font-bold transition-colors duration-200"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.submenu && (
                    <div className="ml-4 space-y-1">
                      {item.submenu.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.href}
                          className="block py-1 text-sm text-gray-200 hover:text-yellow-300 transition-colors duration-200"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Close dropdown on outside click */}
      {openDropdown !== null && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </header>
  );
};

export default Header;